import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Minus, Plus, BookOpen } from "lucide-react";
import { ENSERES_ITEMS, ENSERES_GRANDES, MAX_ENSERES_ITEMS, MAX_BULTOS_PODA, PODA_TIPOS, PODA_UNIDAD } from "@/mock";
import { validateCif } from "@/lib/dni";
import NormasModal from "./NormasModal";

const OTRO_MUEBLE_GRANDE = "Otro mueble de grandes dimensiones";

export default function Step1Details({ data, update, onNext, onBack }) {
  const isEnseres = data.tipo_servicio === "enseres";
  const isEmpresa = isEnseres && data.tipo_solicitante === "empresa";
  const [qty, setQty] = useState(data.enseres_qty || {});
  const [empresaQty, setEmpresaQty] = useState(isEmpresa ? Number(data.enseres_lista?.[0]?.match(/\((?:x)?(\d+)\)/)?.[1] || 0) : 0);
  const grandeInicial = data.enseres_lista?.[0] || "";
  const esOtroGrande = grandeInicial && !ENSERES_GRANDES.slice(0, -1).includes(grandeInicial);
  const [muebleGrande, setMuebleGrande] = useState(esOtroGrande ? OTRO_MUEBLE_GRANDE : grandeInicial);
  const [muebleGrandeDesc, setMuebleGrandeDesc] = useState(esOtroGrande ? grandeInicial : "");
  const [modo, setModo] = useState(data.es_mueble_grande ? "grande" : "varios");
  const [showNormas, setShowNormas] = useState(false);
  const [normasAbiertas, setNormasAbiertas] = useState(!!data.acepta_normas);

  const totalQty = Object.values(qty).reduce((a, b) => a + b, 0);

  const changeQty = (item, delta) => {
    if (delta > 0 && totalQty >= MAX_ENSERES_ITEMS) return;
    const next = { ...qty, [item]: Math.max(0, (qty[item] || 0) + delta) };
    setQty(next);
  };

  const muebleGrandeFinal = muebleGrande === OTRO_MUEBLE_GRANDE ? muebleGrandeDesc.trim() : muebleGrande;

  const canContinue = (() => {
    if (!normasAbiertas || !data.acepta_normas) return false;
    if (isEnseres) {
      if (isEmpresa) {
        return (
          empresaQty > 0 &&
          empresaQty <= MAX_ENSERES_ITEMS &&
          (data.razon_social || "").trim().length > 1 &&
          (data.direccion_empresa || "").trim().length > 2 &&
          validateCif(data.cif || "")
        );
      }
      return modo === "grande" ? muebleGrandeFinal.length > 2 : totalQty > 0 && totalQty <= MAX_ENSERES_ITEMS;
    }
    return data.tipo_vegetacion && (data.cantidad_bultos || 0) > 0 && (data.cantidad_bultos || 0) <= MAX_BULTOS_PODA;
  })();

  const handleNext = () => {
    if (isEnseres) {
      if (isEmpresa) {
        update({ enseres_lista: [`Enseres de pequeño tamaño (x${empresaQty})`], es_mueble_grande: false, enseres_qty: {} });
      } else if (modo === "grande") {
        update({ enseres_lista: [muebleGrandeFinal], es_mueble_grande: true, enseres_qty: {} });
      } else {
        const lista = Object.entries(qty)
          .filter(([, v]) => v > 0)
          .map(([item, v]) => `${item} (x${v})`);
        update({ enseres_lista: lista, es_mueble_grande: false, enseres_qty: qty });
      }
    }
    onNext();
  };

  const unidadLabel = PODA_UNIDAD[data.tipo_vegetacion] || "bolsas/atillos";

  const abrirNormas = () => {
    setShowNormas(true);
    setNormasAbiertas(true);
  };

  return (
    <div>
      <h2 className="font-heading font-bold text-2xl text-municipal mb-2">
        {isEnseres ? "¿Qué enseres quieres que recojamos?" : "Cuéntanos sobre los restos de poda"}
      </h2>
      <p className="text-gray-500 mb-6">
        {isEnseres
          ? isEmpresa
            ? "Las empresas pueden solicitar hasta 3 enseres pequeños por servicio."
            : "Máximo 3 enseres pequeños por servicio, o 1 único enser de gran tamaño (sofás, colchones...)."
          : "Selecciona el tipo de resto vegetal y la cantidad de bolsas o atillos."}
      </p>

      {isEnseres && (
        <div className="mb-6">
          <Label className="text-sm font-semibold text-municipal mb-3 block">¿Quién solicita el servicio?</Label>
          <RadioGroup
            value={data.tipo_solicitante || "ciudadano"}
            onValueChange={(v) => update({ tipo_solicitante: v })}
            className="grid sm:grid-cols-2 gap-3"
            data-testid="tipo-solicitante-group"
          >
            <div className={`flex items-center space-x-2 border-2 rounded-xl px-4 py-3 ${(data.tipo_solicitante || "ciudadano") === "ciudadano" ? "border-municipal bg-municipal/5" : "border-gray-200"}`}>
              <RadioGroupItem value="ciudadano" id="solicitante-ciudadano" data-testid="tipo-solicitante-ciudadano" />
              <Label htmlFor="solicitante-ciudadano" className="font-normal cursor-pointer text-sm">Soy un ciudadano</Label>
            </div>
            <div className={`flex items-center space-x-2 border-2 rounded-xl px-4 py-3 ${data.tipo_solicitante === "empresa" ? "border-municipal bg-municipal/5" : "border-gray-200"}`}>
              <RadioGroupItem value="empresa" id="solicitante-empresa" data-testid="tipo-solicitante-empresa" />
              <Label htmlFor="solicitante-empresa" className="font-normal cursor-pointer text-sm">Soy una empresa</Label>
            </div>
          </RadioGroup>
        </div>
      )}

      {isEmpresa && (
        <div className="grid sm:grid-cols-2 gap-5 mb-6">
          <div>
            <Label className="mb-1.5 block">Razón social</Label>
            <Input
              value={data.razon_social || ""}
              onChange={(e) => update({ razon_social: e.target.value })}
              placeholder="Nombre de la empresa"
              data-testid="wizard-razon-social-input"
            />
          </div>
          <div>
            <Label className="mb-1.5 block">CIF</Label>
            <Input
              value={data.cif || ""}
              onChange={(e) => update({ cif: e.target.value.toUpperCase() })}
              placeholder="B12345678"
              data-testid="wizard-cif-input"
            />
            {data.cif && !validateCif(data.cif) && (
              <p className="text-xs text-red-500 mt-1">CIF no válido</p>
            )}
          </div>
          <div className="sm:col-span-2">
            <Label className="mb-1.5 block">Dirección de la empresa</Label>
            <Input
              value={data.direccion_empresa || ""}
              onChange={(e) => update({ direccion_empresa: e.target.value })}
              placeholder="Dirección del local o negocio en San Fernando de Henares"
              data-testid="wizard-direccion-empresa-input"
            />
          </div>
        </div>
      )}

      {isEnseres ? (
        <>
          {isEmpresa ? (
            <div className="mb-6">
              <Label className="mb-2 block">Número de enseres (máximo {MAX_ENSERES_ITEMS})</Label>
              <div className="flex items-center gap-4 border border-gray-200 rounded-xl px-4 py-3 w-fit" data-testid="empresa-enseres-contador">
                <button type="button" onClick={() => setEmpresaQty((q) => Math.max(0, q - 1))} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-6 text-center font-semibold text-municipal text-lg">{empresaQty}</span>
                <button
                  type="button"
                  onClick={() => setEmpresaQty((q) => Math.min(MAX_ENSERES_ITEMS, q + 1))}
                  disabled={empresaQty >= MAX_ENSERES_ITEMS}
                  className="w-8 h-8 rounded-full bg-municipal text-white flex items-center justify-center hover:bg-municipal-dark disabled:opacity-30"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <>
              <RadioGroup
                value={modo}
                onValueChange={(v) => setModo(v)}
                className="grid sm:grid-cols-2 gap-3 mb-6"
                data-testid="enseres-modo-group"
              >
                <div className={`flex items-center space-x-2 border-2 rounded-xl px-4 py-3 ${modo === "varios" ? "border-municipal bg-municipal/5" : "border-gray-200"}`}>
                  <RadioGroupItem value="varios" id="modo-varios" data-testid="enseres-modo-varios" />
                  <Label htmlFor="modo-varios" className="font-normal cursor-pointer text-sm">Hasta 3 enseres pequeños</Label>
                </div>
                <div className={`flex items-center space-x-2 border-2 rounded-xl px-4 py-3 ${modo === "grande" ? "border-municipal bg-municipal/5" : "border-gray-200"}`}>
                  <RadioGroupItem value="grande" id="modo-grande" data-testid="enseres-modo-grande" />
                  <Label htmlFor="modo-grande" className="font-normal cursor-pointer text-sm">1 enser de gran tamaño (sofá, colchón...)</Label>
                </div>
              </RadioGroup>

              {modo === "varios" ? (
                <div className="mb-6">
                  <p className="text-xs text-gray-400 mb-3" data-testid="enseres-contador">
                    Seleccionados: <strong className="text-municipal">{totalQty}/{MAX_ENSERES_ITEMS}</strong>
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {ENSERES_ITEMS.map((item) => (
                      <div
                        key={item}
                        className="flex items-center justify-between border border-gray-200 rounded-xl px-4 py-3"
                        data-testid={`enseres-item-${item}`}
                      >
                        <span className="text-sm font-medium text-gray-700">{item}</span>
                        <div className="flex items-center gap-3">
                          <button type="button" onClick={() => changeQty(item, -1)} className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200">
                            <Minus className="w-3.5 h-3.5" />
                          </button>
                          <span className="w-5 text-center font-semibold text-municipal">{qty[item] || 0}</span>
                          <button
                            type="button"
                            onClick={() => changeQty(item, 1)}
                            disabled={totalQty >= MAX_ENSERES_ITEMS}
                            className="w-7 h-7 rounded-full bg-municipal text-white flex items-center justify-center hover:bg-municipal-dark disabled:opacity-30"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mb-6">
                  <Label className="mb-2 block">¿Qué enser de gran tamaño es?</Label>
                  <RadioGroup value={muebleGrande} onValueChange={setMuebleGrande} className="grid sm:grid-cols-2 gap-3 mb-3" data-testid="enseres-grande-group">
                    {ENSERES_GRANDES.map((item) => (
                      <div key={item} className="flex items-center space-x-2 border border-gray-200 rounded-xl px-4 py-3">
                        <RadioGroupItem value={item} id={`grande-${item}`} data-testid={`enseres-grande-${item}`} />
                        <Label htmlFor={`grande-${item}`} className="font-normal cursor-pointer text-sm">{item}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                  {muebleGrande === OTRO_MUEBLE_GRANDE && (
                    <Input
                      value={muebleGrandeDesc}
                      onChange={(e) => setMuebleGrandeDesc(e.target.value)}
                      placeholder="Ej: Armario de 3 cuerpos"
                      data-testid="enseres-mueble-grande-input"
                    />
                  )}
                </div>
              )}
            </>
          )}

          <Label className="text-sm font-semibold text-municipal mb-2 block">Observaciones (opcional)</Label>
          <Textarea
            placeholder=""
            value={data.detalles || ""}
            onChange={(e) => update({ detalles: e.target.value })}
            className="mb-6"
            data-testid="wizard-detalles-textarea"
          />
        </>
      ) : (
        <div className="space-y-8 mb-6">
          <div>
            <Label className="text-sm font-semibold text-municipal mb-3 block">Tipo de resto vegetal</Label>
            <RadioGroup
              value={data.tipo_vegetacion}
              onValueChange={(v) => update({ tipo_vegetacion: v })}
              className="grid sm:grid-cols-2 gap-3"
            >
              {PODA_TIPOS.map((t) => (
                <div key={t.value} className="flex items-center space-x-2 border border-gray-200 rounded-xl px-4 py-3">
                  <RadioGroupItem value={t.value} id={`tipo-${t.value}`} />
                  <Label htmlFor={`tipo-${t.value}`} className="font-normal cursor-pointer">{t.label}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>
          <div>
            <Label className="mb-1.5 block">Número de {unidadLabel} (máximo {MAX_BULTOS_PODA})</Label>
            <p className="text-xs font-bold text-municipal mb-1.5" data-testid="poda-max-aviso">
              MÁX. {MAX_BULTOS_PODA} BOLSAS O ATILLOS
            </p>
            <Input
              type="number"
              min="1"
              max={MAX_BULTOS_PODA}
              value={data.cantidad_bultos || ""}
              onChange={(e) => update({ cantidad_bultos: Math.min(MAX_BULTOS_PODA, Number(e.target.value) || 0) || null })}
              placeholder={`Ej: 2 ${unidadLabel}`}
              data-testid="wizard-cantidad-bultos-input"
            />
          </div>
          <div>
            <Label className="text-sm font-semibold text-municipal mb-2 block">Observaciones (opcional)</Label>
            <Textarea
              placeholder=""
              value={data.detalles || ""}
              onChange={(e) => update({ detalles: e.target.value })}
              data-testid="wizard-detalles-textarea"
            />
          </div>
        </div>
      )}

      <div className="flex items-start gap-3 bg-municipal/5 border border-municipal/20 rounded-xl p-4 mb-2">
        <Checkbox
          id="acepta-normas"
          checked={data.acepta_normas || false}
          onCheckedChange={(v) => update({ acepta_normas: v })}
          disabled={!normasAbiertas}
          data-testid="wizard-acepta-normas-checkbox"
        />
        <Label htmlFor="acepta-normas" className={`font-normal text-sm leading-relaxed ${normasAbiertas ? "text-gray-600 cursor-pointer" : "text-gray-400 cursor-not-allowed"}`}>
          He leído y acepto las{" "}
          <button type="button" onClick={abrirNormas} className="text-municipal underline inline-flex items-center gap-1" data-testid="ver-normas-btn">
            <BookOpen className="w-3.5 h-3.5" /> normas del servicio
          </button>.
        </Label>
      </div>
      <p className="text-xs text-dorado mb-8 min-h-[1rem]" data-testid="normas-pendiente-aviso">
        {!normasAbiertas && "Debes abrir y leer las normas del servicio antes de continuar."}
      </p>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} className="border-municipal text-municipal">Atrás</Button>
        <Button disabled={!canContinue} onClick={handleNext} className="bg-municipal hover:bg-municipal-dark text-white" data-testid="wizard-step1-next">
          Continuar
        </Button>
      </div>

      <NormasModal open={showNormas} onClose={() => setShowNormas(false)} servicio={data.tipo_servicio} tipoSolicitante={data.tipo_solicitante} />
    </div>
  );
}
